import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, inject, OnDestroy, signal, Signal, TemplateRef, viewChild, ViewContainerRef } from '@angular/core';
import { AIService } from '@api/index';
import { NgbActiveModal, NgbModalModule } from '@ng-bootstrap/ng-bootstrap';
import { catchError, delay, of, Subscription, tap, timer } from 'rxjs';
import { TENANT_ID } from 'src/app/authorization/authorization-factories';
import { GlobalShortcutKeys } from 'src/app/shared/enumerations/global.shortcutKeys';
import languageDictionary from 'src/app/shared/transloco/default';
import { TranslationService } from 'src/app/shared/transloco/services/translation.service';
import { TranslocoRootModule } from 'src/app/shared/transloco/transloco-root.module';
import { NestedPropertyOf } from 'src/app/shared/transloco/types/nested-key-of.type';
import { ShortcutKeysService } from '../../shortcutKeys/shortcutKeys.service';
import { ContentViewState } from '../models/content-view-state';

@Component({
    selector: 'app-ai-assistant-dialog',
    standalone: true,
    imports: [NgbModalModule, CommonModule, TranslocoRootModule],
    templateUrl: './ai-assistant-dialog.component.html',
    styleUrl: './ai-assistant-dialog.component.scss',
})
export class AiAssistantDialogComponent implements AfterViewInit, OnDestroy {
    private subscriptions = new Subscription();
    private translationService = inject(TranslationService);
    private shortcutKeysService = inject(ShortcutKeysService);
    private tenantId: string = inject(TENANT_ID);
    private aiService = inject(AIService);
    private userPromptRef: Signal<ElementRef<HTMLTextAreaElement>> = viewChild('userPrompt');
    private loaderRandomMessageRef = viewChild('loaderRandomMessageRef', { read: ViewContainerRef });
    private loaderMessageTemplateRef = viewChild('loaderMessageTemplateRef', { read: TemplateRef });

    public activeModal = inject(NgbActiveModal);
    public contentViewState = signal(ContentViewState.UserQuery);
    public ContentViewState = ContentViewState;
    public errorMessage = signal('');

    public ngAfterViewInit(): void {
        this.registerShortcutKeys();
        this.userPromptRef().nativeElement.focus();
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public submitUserPrompt() {
        const timerSubscription = new Subscription();
        const userPrompt = this.userPromptRef().nativeElement.value;
        this.contentViewState.set(ContentViewState.Loader);
        this.displayLoaderMessages(timerSubscription);

        this.subscriptions.add(
            this.aiService
                .sendPrompt(this.tenantId, userPrompt)
                .pipe(
                    tap((response) => {
                        this.activeModal.close(response);
                    }),
                    this.handleErrors(),
                    tap(() => {
                        timerSubscription.unsubscribe();

                        if (!this.userPromptRef()) {
                            return;
                        }

                        this.userPromptRef().nativeElement.value = userPrompt;
                        this.onUserPromptChange();
                    })
                )
                .subscribe()
        );
    }

    public onUserPromptChange(): void {
        const textarea = this.userPromptRef().nativeElement;
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
    }

    private registerShortcutKeys() {
        this.subscriptions.add(
            this.translationService.afterLanguageChanged$
                .pipe(
                    tap(() => {
                        const aiAssistantLabel = this.translationService.translate('shortcutKeys.aiAssistant');
                        const shortcuts = [this.shortcutKeysService.registerShortcut(GlobalShortcutKeys.Ok, () => this.submitUserPrompt(), aiAssistantLabel, this.translationService.translate('actions.ok'))];

                        this.shortcutKeysService.unregisterShortcuts(aiAssistantLabel);
                        this.shortcutKeysService.registerShortcuts(shortcuts);
                    })
                )
                .subscribe()
        );
    }

    private displayLoaderMessages(timerSubscription: Subscription) {
        timerSubscription.add(
            timer(0, 10000)
                .pipe(
                    tap((num) => {
                        if (this.loaderRandomMessageRef() == null) {
                            return;
                        }

                        const targetLoaderMessageId = num % 20;
                        const translationKey = `aiAssistant.loaderMessages.${targetLoaderMessageId}` as NestedPropertyOf<typeof languageDictionary>;
                        const loaderText = this.translationService.translate(translationKey);
                        this.loaderRandomMessageRef().clear();
                        this.loaderRandomMessageRef().createEmbeddedView(this.loaderMessageTemplateRef(), { $implicit: { message: loaderText } });
                    })
                )
                .subscribe()
        );
    }

    private handleErrors() {
        return catchError((errorResponse: HttpErrorResponse) => {
            this.contentViewState.set(ContentViewState.Error);
            const errorMessage = (errorResponse.error?.detail ?? '') as string;

            if (errorMessage.includes('No connection could be made because the target machine actively refused it')) {
                this.errorMessage.set(this.translationService.translate('aiAssistant.errors.serviceNotReachable'));
            } else if (errorMessage.includes('The request was canceled due to the configured')) {
                this.errorMessage.set(this.translationService.translate('aiAssistant.errors.timeout'));
            } else if (errorMessage.includes('Response body does not contain a valid JSON')) {
                this.errorMessage.set(this.translationService.translate('aiAssistant.errors.invalidJSONReceived'));
            } else {
                this.errorMessage.set(this.translationService.translate('aiAssistant.errors.unexpectedError'));
            }

            return of(errorResponse).pipe(delay(10));
        });
    }
}
